import { IContactFormData } from 'src/SharedCustomHooks/UseContactFormData/types';
import { selectFormOptions } from 'src/store/selectors/formOptions';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { IContactFormDataForAPI, IContactPayloadForAPI } from 'src/utils/CommonHelpers/ContactAPIOperations/types';
import {
  addCalendarFields,
  addHomePropertyFields,
  addOccupantFields,
  addRealtorFields,
  addServiceManagerFields,
  addServiceProviderFields,
  createBasePayload,
} from 'src/utils/CommonHelpers/ContactAPIOperations/sharedContactPayloadBuilderHelpers';
import { toast } from 'react-toastify';
import { addExistingHomeownerToSuite, saveContact, updateContact } from 'src/services/client';
import {
  handleContactsAPIResponse,
  handleImageUpload,
  transformContactFormDataForAPI,
} from 'src/utils/CommonHelpers/ContactAPIOperations/commonContactDataHelpers';
import { buildSuitesPayload } from 'src/pages/SharedPageCustomHooks/UseContactSaveSuitesPage/helpers';

export interface UseContactAPIOperationsSuitesReturn {
  isLoading: boolean;
  response: any;
  error: string | null;
  
  onCreateContact: (
    formData: IContactFormData,
    propertyId: string,
    suiteId: string
  ) => Promise<boolean>;
  
  onEditContact: (
    formData: IContactFormData,
    propertyId: string,
    suiteId: string
  ) => Promise<boolean>;
  
  addExistingUserToSuite: (
    formData: IContactFormData,
    propertyId: string,
    suiteId: string
  ) => Promise<boolean>;
  
  clearResponse: () => void;
}

export const useContactAPIOperationsSuites = (): UseContactAPIOperationsSuitesReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  
  const adminUser = useSelector(selectAuthenticatedUser);
  const formOptions = useSelector(selectFormOptions);
  
  const onCreateContact = async (
    formData: IContactFormData,
    propertyId: string,
    suiteId: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    setResponse(null);
    
    try {
      const imageUrl = await handleImageUpload(formData);
      const apiFormData: IContactFormDataForAPI = transformContactFormDataForAPI(formData, imageUrl);
      const payload: IContactPayloadForAPI = buildSuitesPayload(
        apiFormData,
        propertyId,
        suiteId,
        adminUser,
        formOptions
      );
      
      const response: any = await saveContact(payload);
      return handleContactsAPIResponse(response, true, setResponse, setError);
    } catch (err: any) {
      console.error('Contact creation failed:', err);
      
      const errorMessage =
        err.message || 'Something went wrong, please try again.';
      setError(errorMessage);
      
      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
      
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  const onEditContact = async (
    formData: IContactFormData,
    propertyId: string,
    suiteId: string
  ): Promise<boolean> => {
    if (!formData.id) {
      const error = 'Cannot edit contact: Missing contact ID';
      console.error(error);
      toast.error(`🚫 ${error}`, {
        autoClose: 8000,
        position: 'top-center',
      });
      return false;
    }
    
    setIsLoading(true);
    setError(null);
    setResponse(null);
    
    try {
      const imageUrl = await handleImageUpload(formData);
      const apiFormData: IContactFormDataForAPI = transformContactFormDataForAPI(formData, imageUrl);
      
      const payload: IContactPayloadForAPI = buildSuitesPayload(
        apiFormData,
        propertyId,
        suiteId,
        adminUser,
        formOptions
      );
      
      const response: any = await updateContact(payload);
      return handleContactsAPIResponse(response, true, setResponse, setError);
    } catch (err: any) {
      console.error('Contact update failed:', err);
      
      const errorMessage =
        err.message || 'Something went wrong, please try again.';
      setError(errorMessage);
      
      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
      
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  const addExistingUserToSuite = async (
    formData: IContactFormData,
    propertyId: string,
    suiteId: string
  ): Promise<boolean> => {
    const requestPayload = {
      SuiteId: suiteId,
      PropertyId: propertyId,
      Role: formData.userRole,
      UserId: formData.id,
      IsPrimaryContact: formData.isPrimaryContact,
      CreatedBy: adminUser.id,
    };
    
    try {
      const response: any = await addExistingHomeownerToSuite(requestPayload);
      
      if (response.status === 200 || response.ok) {
        const responseJson = await response.json();
        
        if (responseJson.statusCode === 200) {
          toast.success('ⓘ User added to suite successfully!', {
            autoClose: 10000,
            position: 'top-center',
          });
          return true;
        } else {
          throw new Error(
            `API returned status code: ${responseJson.statusCode}`
          );
        }
      } else {
        throw new Error(`Request failed with status: ${response.status}`);
      }
    } catch (err: any) {
      console.error('Adding existing user to suite failed:', err);
      const errorMessage =
        err.message || 'Something went wrong, please try again.';
      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
      throw err;
    }
  };
  
  const clearResponse = () => {
    setResponse(null);
    setError(null);
  };
  
  return {
    isLoading,
    response,
    error,
    onCreateContact,
    onEditContact,
    addExistingUserToSuite,
    clearResponse,
  };
}